import { CHAT_TRIGGER_NODE_TYPE, EXECUTE_WORKFLOW_TRIGGER_NODE_TYPE, FORM_TRIGGER_NODE_TYPE, MANUAL_TRIGGER_NODE_TYPES, MCP_TRIGGER_NODE_TYPE, WEBHOOK_NODE_TYPE, } from './constants';
import { toExecutionContextEstablishmentHookParameter } from './execution-context-establishment-hooks';
const NO_IDENTITY = {
    providesN8nIdentity: false,
    providesExternalIdentity: false,
};
/** Whether a trigger's parameters declare at least one context establishment hook. */
function hasContextEstablishmentHook(parameters) {
    const hookParams = toExecutionContextEstablishmentHookParameter(parameters);
    return (hookParams !== null &&
        hookParams.success &&
        hookParams.data.contextEstablishmentHooks.hooks.length > 0);
}
/**
 * Classifies a single trigger node by the identity it can establish at runtime.
 *
 * Shared by the backend publish-time validation (`WorkflowValidationService`) and
 * the editor's trigger-compatibility warning so the two can't drift. Keep it in
 * sync with how the engine establishes identity (`execution-context.ts`,
 * manual/parent inheritance) and the resolvers' identifiers (e.g. `N8NIdentifier`):
 * when a new trigger or identity source is added there, reflect it here too.
 */
export function classifyTriggerIdentity(nodeType, parameters, options = {}) {
    // Sub-workflows inherit identity from the parent; Chat Hub and MCP-over-n8nOAuth2
    // inject it. All provide both identity families.
    const isSubWorkflowTrigger = nodeType === EXECUTE_WORKFLOW_TRIGGER_NODE_TYPE;
    const isChatHubTrigger = nodeType === CHAT_TRIGGER_NODE_TYPE && parameters?.availableInChat === true;
    const isMcpTrigger = nodeType === MCP_TRIGGER_NODE_TYPE && parameters?.authentication === 'n8nOAuth2';
    // The Webhook node's opt-in "n8n User Auth (OAuth2)" mode injects the caller's
    // n8n identity the same way the MCP trigger does — sharing the `n8nOAuth2` value.
    // Unlike the MCP trigger, this mode is gated by an instance flag (the option is
    // hidden in the editor while it's off), so a caller must opt in with
    // `isWebhookOAuth2Enabled` for it to count — omitting it fails closed.
    const isOAuth2Webhook = nodeType === WEBHOOK_NODE_TYPE &&
        parameters?.authentication === 'n8nOAuth2' &&
        options.isWebhookOAuth2Enabled === true;
    // The form trigger only establishes an identity through its OAuth2 flow, so callers
    // must opt the branch in with the instance's `isFormOAuth2Enabled`. Omitting it fails
    // closed: without the flag a `n8nUserAuth` form takes the legacy cookie/HMAC path,
    // which authenticates the submitter but establishes no identity to resolve
    // credentials with — publish must reject that instead of failing mid-execution.
    const isFormTrigger = nodeType === FORM_TRIGGER_NODE_TYPE &&
        parameters?.authentication === 'n8nUserAuth' &&
        options.isFormOAuth2Enabled === true;
    if (isSubWorkflowTrigger ||
        isChatHubTrigger ||
        isMcpTrigger ||
        isFormTrigger ||
        isOAuth2Webhook) {
        return { providesN8nIdentity: true, providesExternalIdentity: true };
    }
    // Manual/chat/MCP triggers run with the n8n user identity.
    if (MANUAL_TRIGGER_NODE_TYPES.includes(nodeType)) {
        return { providesN8nIdentity: true, providesExternalIdentity: false };
    }
    // Any other trigger with a context establishment hook provides an external identity.
    if (hasContextEstablishmentHook(parameters)) {
        return { providesN8nIdentity: false, providesExternalIdentity: true };
    }
    return NO_IDENTITY;
}
//# sourceMappingURL=trigger-identity.js.map